import { useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { persistClickIdsFromCurrentUrl } from "../../../../utils/trackingParams";

export const useAppUtmParams = (getPromoCode: () => Promise<any>) => {
  const [searchParams] = useSearchParams();
  const { utmParams, setUtmParams, utmSource, setUtmSource, accessJWT }: iGlobalContext = useContext(StateContext);

  const trackingParams = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_adset",
    "fbclid",
    "gclid",
    "gad_source",
    "gad_campaignid",
    "pc",
    "fpr",
    "fpc",
  ];

  function getCurrentUtmSource() {
    const fullUrl = window.location.href;

    // Split by ? and check each part
    const parts = fullUrl.split("?");

    for (let i = 1; i < parts.length; i++) {
      const params = new URLSearchParams(parts[i].split("#")[0]); // Remove hash if present
      const utmSource = params.get("utm_source");
      if (utmSource) {
        return utmSource;
      }
    }

    return null;
  }

  const paramValues = trackingParams.reduce((acc: Record<string, string>, param: string) => {
    const value = searchParams.get(param);
    if (value) acc[param] = value;
    return acc;
  }, {});

  const hasTrackingParams = Object.keys(paramValues).length > 0;

  const updateUtmParams = () => {
    if (hasTrackingParams) {
      const newParams = new URLSearchParams(paramValues);
      let currentUtmSource;
      // Promo lookup moved to the token-gated effect below — /promos/search is
      // auth-protected, so it must wait for accessJWT (and retry when it lands).
      if (newParams.toString() && searchParams.get("utm_source") === null) {
        currentUtmSource = getCurrentUtmSource();
      }
      if (currentUtmSource) {
        searchParams.set("utm_source", currentUtmSource);
      }

      let finalParams: URLSearchParams;
      if (utmParams && utmParams.length > 0) {
        finalParams = new URLSearchParams(utmParams);

        newParams.forEach((value, key) => {
          finalParams.set(key, value);
        });
      } else {
        finalParams = new URLSearchParams(newParams);
      }

      const existingUtmSourceInParams = finalParams.get("utm_source");
      const sourceToUse = utmSource || existingUtmSourceInParams || currentUtmSource;

      if (sourceToUse) {
        finalParams.set("utm_source", sourceToUse);
        if (sourceToUse !== utmSource) {
          setUtmSource(sourceToUse);
        }
      }

      const finalParamsString = finalParams.toString();

      if (finalParamsString !== utmParams) {
        setUtmParams(finalParamsString);
      }
    }
  };

  useEffect(() => {
    persistClickIdsFromCurrentUrl();
  }, []);

  useEffect(() => {
    if (hasTrackingParams) {
      updateUtmParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTrackingParams]); // Added hasTrackingParams to dependency array to match original logic "if (hasTrackingParams) updateUtmParams()" which was outside effect but effectively ran on render.
  // Wait, in original code:
  // if (hasTrackingParams) { updateUtmParams(); }
  // useEffect(() => { updateUtmParams(); }, [searchParams, utmParams]);

  // So I should replicate that.

  useEffect(() => {
    updateUtmParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, utmParams]);

  // Fire the promo lookup only once a token exists. Keyed on both signals so it
  // runs when tracking params first land AND retries when the guest/login token
  // arrives late — fixing the race where /promos/search went out without an
  // x-access-token (500) and the offer silently never loaded. getPromoCode
  // self-guards against a refetch, so a double-fire is a no-op.
  useEffect(() => {
    if (hasTrackingParams && accessJWT) {
      getPromoCode().catch((err) => console.warn("Promo code fetch failed:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTrackingParams, accessJWT]);

  return { paramValues };
};
